import { connect } from 'react-redux';
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import TableFilter from "src/components/TableFilter";
import { REPORT_STATUS_LIST } from 'src/components/constants';
import { 
    API_URL_FETCH_RISK_RATINGS,
    CM_API_URL_FETCH_RISK_RATINGS,
    API_URL_ADMIN_GET_ASSIGN_ANALYST_LIST,
    CM_API_URL_ADMIN_GET_ASSIGN_ANALYST_LIST
} from 'src/common/constants';
import axios from 'axios';
import { isFeatureEnabledForActiveModule } from 'src/helpers/module_helper';

const FilteringComponent = ({
    t,
    modules,
    token,
    categoriesQuery,
    onChange
}) => {
    const [tableFilters, setTableFilters] = useState({
        status: null,
        categories: [],
        risk: null,
        exclude: [],
        assignedAnalyst: null,
        anonymization: [],
        caseId: null,
        search: null
    });

    const isFirstRender = useRef(true);

    const {
        isFetching: risksRatingsListIsFetching,
        data: risksRatingsList
    } = useQuery({
        queryKey: [
            "app-fetch-risks-ratings-list",
            modules.active
        ],
        queryFn: async () => {
            const respones = await axios.get(
                modules.active === "wb"
                    ? API_URL_FETCH_RISK_RATINGS
                    : CM_API_URL_FETCH_RISK_RATINGS,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            return respones.data?.data;
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: modules.featuresLoadingStatus === 'loaded' && isFeatureEnabledForActiveModule('risk-assessment') && isFeatureEnabledForActiveModule('filter-risk'),
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching risk ratings."), {
                type: "error"
            });
        }
    });

    const {
        isFetching: analystsAreFetching,
        data: analystsList
    } = useQuery({
        queryKey: [
            "app-fetch-analysts-and-admin-analysts-list",
            modules.active
        ],
        queryFn: async () => {
            const response = await axios.post(
                modules.active === 'wb' ? API_URL_ADMIN_GET_ASSIGN_ANALYST_LIST : CM_API_URL_ADMIN_GET_ASSIGN_ANALYST_LIST,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            return response.data?.data;
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("Failed to fetch analysts and analyst admins. please try again."), {
                type: "error"
            });
        }
    });

    const tableFilterTabs = useMemo(() => {
        const items = [];

        if (isFeatureEnabledForActiveModule(
            "fliter-status"
        )){
            items.push({
                type: 'radio_list',
                name: 'status',
                title: 'Status',
                icon: <i className="ri-toggle-line" />,
                defaultValue: tableFilters?.status,
                data: REPORT_STATUS_LIST,
                options: {
                    disableAll: false
                },
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return t(item.titleKey)
                }
            })
        }

        if (isFeatureEnabledForActiveModule(
            "filter-category"
        )){
            items.push({
                type: 'check_list',
                name: 'categories',
                title: 'Category',
                icon: <i className="ri-filter-3-line" />,
                isLoading: categoriesQuery.isFetching,
                defaultValue: tableFilters?.categories,
                data: categoriesQuery.data || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return t(item.title)
                }
            })
        }

        if(modules.featuresLoadingStatus === 'loaded' && isFeatureEnabledForActiveModule('filter-risk')){
            let risksRatings = [];

            if(isFeatureEnabledForActiveModule('risk-assessment')){
                risksRatings = risksRatingsList || [];
            }
            else{
                risksRatings = [
                    {
                        id: "high",
                        name: "High"
                    },
                    {
                        id: "medium",
                        name: "Medium"
                    },
                    {
                        id: "low",
                        name: "Low"
                    }
                ];
            }

            items.push({
                type: 'radio_list',
                name: 'risk',
                title: 'Risk',
                icon: <i className="ri-bar-chart-line" />,
                isLoading: risksRatingsListIsFetching,
                defaultValue: tableFilters?.risk,
                data: risksRatings || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return t(item.name)
                }
            })
        }

        return [
            ...items,
            {
                type: 'radio_list',
                name: 'assignedAnalyst',
                title: 'Analyst',
                icon: <i className="ri-user-line" />,
                isLoading: analystsAreFetching,
                defaultValue: tableFilters?.assignedAnalyst,
                data: analystsList || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return `${item.first_name} ${item.last_name}`
                }
            },
            {
                type: 'check_list',
                name: 'exclude',
                title: 'Exclude',
                icon: <i className="ri-close-circle-line" />,
                isLoading: false,
                defaultValue: tableFilters?.exclude,
                data: [
                    {
                        id: 'excludeDeleted',
                        title: 'Deleted cases'
                    },
                    {
                        id: 'excludeTestCases',
                        title: 'Test Cases'
                    }
                ],
                searchInListOption: {
                    enabled: false,
                    remote: false
                },
                formatter: (item) => {
                    return t(item.title)
                }
            },
            {
                type: 'check_list',
                name: 'anonymization',
                title: 'Anonymization',
                icon: <i className="ri-eye-off-line" />,
                isLoading: false,
                defaultValue: tableFilters?.anonymization,
                data: [
                    {
                        id: 'onlyAnonymized',
                        title: 'Show anonymized items only'
                    }
                ],
                searchInListOption: {
                    enabled: false,
                    remote: false
                },
                formatter: (item) => {
                    return t(item.title)
                }
            },
            {
                type: 'search_textbox',
                name: 'caseId',
                title: t('Case ID'),
                icon: <i className="ri-terminal-box-line" />,
                defaultValue: tableFilters?.caseId,
                options: {
                    placeholder: t('Case ID'),
                    typingDelay: 750
                },
                displayIndependently: true,
                onFilterChange: (value) => {
                    setTableFilters((currentState) => ({ ...currentState, caseId: value }))
                }
            },
            {
                type: 'search_textbox',
                name: 'search',
                title: t('Description / Keywords'),
                icon: <i className="ri-search-line" />,
                defaultValue: tableFilters?.search,
                options: {
                    placeholder: t('Description / Keywords'),
                    typingDelay: 750
                },
                displayIndependently: true,
                onFilterChange: (value) => {
                    setTableFilters((currentState) => ({
                        ...currentState,
                        search: value
                    }))
                }
            }            
        ];
    }, [
        tableFilters,
        categoriesQuery.isFetching,
        categoriesQuery.data,
        risksRatingsListIsFetching,
        risksRatingsList,
        analystsAreFetching,
        analystsList,
        modules.featuresLoadingStatus,
        t
    ]);

    const onTableFilterOkButtonClicked = (filters, independentTabsData) => {
        if (filters) {
            const result = {}

            if ('status' in filters) {
                result.status = filters.status !== 'all' ? filters.status : undefined
            }

            result.categories = filters.categories.length > 0 ? filters.categories : undefined

            result.exclude = filters.exclude.length > 0 ? filters.exclude : undefined

            if ('risk' in filters) {
                result.risk = filters.risk !== 'all' ? filters.risk : undefined
            }

            if ('assignedAnalyst' in filters) {
                result.assignedAnalyst = filters.assignedAnalyst !== 'all' ? filters.assignedAnalyst : undefined
            }

            result.anonymization = filters.anonymization.length > 0 ? filters.anonymization : undefined
            
            setTableFilters({
                ...tableFilters,
                ...result
            })
        } else {
            setTableFilters({
                status: null,
                categories: [],
                risk: null,
                exclude: [],
                assignedAnalyst: null,
                anonymization: [],
                search: independentTabsData?.search,
                caseId: independentTabsData?.caseId
            })
        }
    }

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        onChange && onChange(tableFilters)
    }, [ 
        tableFilters,
        onChange
    ]);

    return (
        <TableFilter
            defaultActiveTab='statuts'
            tabs={tableFilterTabs}
            onOkButtonClicked={
                onTableFilterOkButtonClicked
            }
        />
    )
}

const mapStatetoProps = (state) => {
    const { 
		Modules: modules 
	} = state;
    const { token } = state.Login;

	return {
        modules,
        token
	};
};

export default withNamespaces()(
    connect(
        mapStatetoProps
    )(FilteringComponent)
);